/**
 * AdminSweepProgress — /admin/sweeps
 *
 * Bird's-eye view of the India-wide Geographic Discovery Engine. Shows
 * per-state catalog size, sweep status, and live doc counts (locations
 * + POIs). Auto-refreshes every 30 s.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Play, RefreshCcw, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLORS = {
  complete: { bg: "#DCFCE7", fg: "#166534" },
  running:  { bg: "#FEF3C7", fg: "#92400E" },
  idle:     { bg: "#F1F5F9", fg: "#334155" },
  failed:   { bg: "#FEE2E2", fg: "#991B1B" },
};

export default function AdminSweepProgress() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ locations: 0, pois: 0 });
  const [loading, setLoad] = useState(true);
  const [kickingKey, setKick] = useState(null);

  const load = () => {
    http.get("/geo/sweep/progress")
      .then((r) => {
        setRows(r.data?.regions || []);
        setTotals(r.data?.totals || { locations: 0, pois: 0 });
      })
      .catch(() => setRows([]))
      .finally(() => setLoad(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const kick = async (key) => {
    setKick(key);
    try {
      await http.post(`/geo/sweep/${key}`);
      toast.success(`Sweep queued for ${key}`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't kick sweep");
    } finally { setKick(null); }
  };

  if (!user || !(user.roles || []).includes("admin")) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-sm opacity-70" data-testid="admin-sweeps-forbidden">Admin only.</div>;
  }

  return (
    <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-sweep-progress">
      <div className="flex items-center gap-2 mb-2">
        <Globe2 className="w-5 h-5" />
        <h1 className="font-display text-3xl">Geographic Discovery — Live</h1>
      </div>
      <p className="text-sm opacity-70 mb-6">
        Per-state view of the India-wide sweep. Auto-refreshes every 30 seconds.
      </p>

      {/* Totals hero */}
      <div className="rounded-2xl border p-5 mb-6 grid grid-cols-2 gap-6"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
        data-testid="admin-sweep-totals">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-60">Total locations</div>
          <div className="text-3xl font-display" data-testid="admin-sweep-total-locations">{totals.locations.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-60">Total POIs</div>
          <div className="text-3xl font-display" data-testid="admin-sweep-total-pois">{totals.pois.toLocaleString()}</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs opacity-70">{rows.length} regions in catalog</div>
        <button onClick={load} className="wofy-btn-ghost text-xs" data-testid="admin-sweep-refresh">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh now
        </button>
      </div>

      {loading && <div className="text-sm opacity-70 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase font-mono opacity-70">
            <tr style={{ background: "var(--wofy-surface)" }}>
              <th className="text-left px-3 py-2">Region</th>
              <th className="text-right px-3 py-2">Catalog</th>
              <th className="text-right px-3 py-2">Locations</th>
              <th className="text-right px-3 py-2">POIs</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const c = STATUS_COLORS[r.status] || STATUS_COLORS.idle;
              const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
              return (
                <tr key={r.key} className="border-t" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-sweep-row-${r.key}`}>
                  <td className="px-3 py-2">
                    <Link to={`/l/india/${r.key}`} className="hover:underline font-medium">
                      {r.key.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase())}
                    </Link>
                  </td>
                  <td className="text-right px-3 py-2 tabular-nums">{r.catalog}</td>
                  <td className="text-right px-3 py-2 tabular-nums" data-testid={`admin-sweep-loc-${r.key}`}>{r.locations}</td>
                  <td className="text-right px-3 py-2 tabular-nums" data-testid={`admin-sweep-poi-${r.key}`}>{r.pois}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{ background: c.bg, color: c.fg }}>
                      {r.status}
                    </span>
                    {r.status === "running" && r.total > 0 && (
                      <span className="ml-2 text-[10px] opacity-70">{r.done}/{r.total} ({pct}%)</span>
                    )}
                  </td>
                  <td className="text-right px-3 py-2">
                    <button
                      onClick={() => kick(r.key)}
                      disabled={kickingKey === r.key || r.status === "running"}
                      className="wofy-btn-ghost text-xs"
                      data-testid={`admin-sweep-kick-${r.key}`}
                    >
                      {kickingKey === r.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      Kick
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </main>
  );
}
